import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { submitAnonymousReport, reportUploadImage, reportUploadVideo, getFileUrl, formatApiError } from '../lib/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Shield, Image as ImageIcon, Video as VideoIcon, X, CheckCircle, Send, ArrowLeft, AlertTriangle, MapPin, Locate } from 'lucide-react';
import { toast } from 'sonner';

const COUNTRIES = ["México", "Estados Unidos", "Guatemala", "Otro"];
const STATES_MX = [
  "Tlaxcala", "Puebla", "CDMX", "Estado de México", "Veracruz", "Hidalgo", "Oaxaca",
  "Jalisco", "Nuevo León", "Guanajuato", "Querétaro", "Morelos", "Guerrero", "Chiapas",
  "Michoacán", "Yucatán", "Quintana Roo", "Sonora", "Chihuahua", "Baja California", "Otro"
];

export default function DenunciaPage() {
  const nav = useNavigate();
  const imgRef = useRef(null);
  const vidRef = useRef(null);
  const [form, setForm] = useState({
    description: '',
    country: 'México',
    state: 'Tlaxcala',
    municipality: '',
    street: '',
    contact: '',
    lat: null,
    lng: null,
  });
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [upImg, setUpImg] = useState(false);
  const [upVid, setUpVid] = useState(false);
  const [sending, setSending] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error('Tu navegador no soporta geolocalización'); return; }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm(p => ({ ...p, lat: pos.coords.latitude, lng: pos.coords.longitude }));
        toast.success('Ubicación capturada');
        setGpsLoading(false);
      },
      (err) => {
        setGpsLoading(false);
        const msg = err.code === 1 ? 'Permiso de ubicación denegado' : err.code === 2 ? 'Ubicación no disponible' : err.code === 3 ? 'Tiempo de espera agotado' : 'Error de geolocalización';
        toast.error(msg);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const clearLocation = () => setForm(p => ({ ...p, lat: null, lng: null }));

  const addImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (images.length + files.length > 5) { toast.error('Máximo 5 imágenes'); return; }
    setUpImg(true);
    for (const f of files) {
      try {
        const { data } = await reportUploadImage(f);
        setImages(prev => [...prev, data.path]);
      } catch (err) {
        toast.error(`Error al subir ${f.name}: ${formatApiError(err.response?.data?.detail)}`);
      }
    }
    setUpImg(false);
    if (imgRef.current) imgRef.current.value = '';
  };

  const addVideos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (videos.length + files.length > 2) { toast.error('Máximo 2 videos'); return; }
    setUpVid(true);
    for (const f of files) {
      try {
        const { data } = await reportUploadVideo(f);
        setVideos(prev => [...prev, data.path]);
      } catch (err) {
        toast.error(`Error al subir ${f.name}: ${formatApiError(err.response?.data?.detail)}`);
      }
    }
    setUpVid(false);
    if (vidRef.current) vidRef.current.value = '';
  };

  const removeImg = (p) => setImages(prev => prev.filter(x => x !== p));
  const removeVid = (p) => setVideos(prev => prev.filter(x => x !== p));

  const submit = async (e) => {
    e.preventDefault(); setError('');
    if (form.description.trim().length < 10) { setError('Describe los hechos (mínimo 10 caracteres)'); return; }
    setSending(true);
    try {
      const payload = { ...form, images, videos };
      if (form.lat == null || form.lng == null) { delete payload.lat; delete payload.lng; }
      const { data } = await submitAnonymousReport(payload);
      setDone(data);
      toast.success('Denuncia enviada');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  if (done) {
    return (
      <div data-testid="denuncia-success" className="max-w-xl mx-auto px-4 py-12">
        <div className="bg-white border-2 border-[#0B1F40] p-8 brutal-shadow text-center">
          <CheckCircle className="h-14 w-14 text-emerald-600 mx-auto mb-4" />
          <h2 className="text-2xl text-[#0B1F40] mb-2" style={{ fontFamily: 'Archivo Black' }}>DENUNCIA RECIBIDA</h2>
          <p className="text-sm text-[#0B1F40]/60 font-bold mb-4">Tu denuncia fue enviada al administrador de RP Noticias.</p>
          <div className="bg-[#F4F5F7] border-l-4 border-[#E50B15] p-3 text-left mb-5">
            <p className="text-[9px] font-black tracking-[0.2em] uppercase text-[#0B1F40]/50">FOLIO DE REFERENCIA</p>
            <p data-testid="ref-code" className="text-2xl font-black text-[#E50B15]" style={{ fontFamily: 'Archivo Black' }}>{done.ref_code}</p>
          </div>
          <p className="text-[11px] text-[#0B1F40]/50 font-bold mb-6">Guarda este folio. Tu identidad permanece anónima.</p>
          <div className="flex gap-2">
            <button onClick={() => { setDone(null); setForm({ description: '', country: 'México', state: 'Tlaxcala', municipality: '', street: '', contact: '', lat: null, lng: null }); setImages([]); setVideos([]); }}
              className="flex-1 bg-[#0B1F40] text-white font-black text-xs h-10 brutal-btn-press" style={{ boxShadow: '3px 3px 0 #E50B15' }}>OTRA DENUNCIA</button>
            <Link to="/" className="flex-1">
              <button className="w-full bg-[#E50B15] text-white font-black text-xs h-10 brutal-btn-press" style={{ boxShadow: '3px 3px 0 #0B1F40' }}>IR AL INICIO</button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const statesList = form.country === 'México' ? STATES_MX : ['Otro'];

  return (
    <div data-testid="denuncia-page" className="max-w-3xl mx-auto px-4 py-8">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-[10px] font-black text-[#0B1F40]/30 hover:text-[#E50B15] tracking-[0.15em] uppercase mb-6">
        <ArrowLeft className="h-3 w-3" /> VOLVER
      </button>

      {/* Hero */}
      <div className="bg-[#0B1F40] p-6 mb-6 relative overflow-hidden" style={{ boxShadow: '6px 6px 0 #E50B15' }}>
        <div className="absolute -right-8 -top-8 opacity-10">
          <Shield className="h-40 w-40 text-[#E50B15]" />
        </div>
        <div className="relative">
          <span className="text-[10px] font-black tracking-[0.3em] uppercase text-[#E50B15]">CIUDADANO</span>
          <h1 className="text-3xl sm:text-4xl tracking-tighter text-white mt-1" style={{ fontFamily: 'Archivo Black' }}>DENUNCIA ANÓNIMA</h1>
          <p className="text-xs text-white/60 mt-3 max-w-lg leading-relaxed">
            Envía fotografías, videos y una descripción de hechos al equipo editorial. Tu identidad permanece anónima salvo que decidas dejar un contacto.
          </p>
        </div>
      </div>

      {/* Warning */}
      <div className="bg-amber-50 border-l-4 border-amber-500 p-3 mb-6 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-[11px] text-amber-900 font-bold leading-relaxed">
          No envíes información falsa. Incluye datos verificables (lugar, fecha). RP Noticias revisará cada denuncia antes de publicar.
        </p>
      </div>

      {error && <div data-testid="denuncia-error" className="mb-4 p-3 bg-red-50 border-l-4 border-[#E50B15] text-[#E50B15] text-xs font-bold">{error}</div>}

      <form onSubmit={submit} className="space-y-5 bg-white border-2 border-[#0B1F40] p-6 brutal-shadow">
        {/* Description */}
        <div>
          <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">DESCRIPCIÓN DE LOS HECHOS *</Label>
          <Textarea data-testid="denuncia-description" value={form.description} onChange={e => set('description', e.target.value)}
            placeholder="Describe qué ocurrió, cuándo, quiénes estaban involucrados..."
            className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 min-h-[140px] font-bold text-sm" required />
          <p className="text-[9px] text-[#0B1F40]/30 mt-1">{form.description.length} / 5000 caracteres</p>
        </div>

        {/* Location */}
        <div className="border-t-2 border-[#0B1F40]/10 pt-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-[10px] font-black tracking-[0.3em] uppercase text-[#E50B15]">UBICACIÓN DE LOS HECHOS</h3>
            {form.lat != null ? (
              <div className="flex items-center gap-2">
                <span data-testid="gps-coords" className="inline-flex items-center gap-1.5 text-[10px] font-black text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1">
                  <MapPin className="h-3 w-3" /> GPS: {form.lat.toFixed(4)}, {form.lng.toFixed(4)}
                </span>
                <button type="button" onClick={clearLocation} className="text-[#E50B15] hover:text-[#0B1F40]" aria-label="Quitar ubicación GPS"><X className="h-3.5 w-3.5" /></button>
              </div>
            ) : (
              <button type="button" data-testid="use-my-location" onClick={useMyLocation} disabled={gpsLoading}
                className="inline-flex items-center gap-1.5 bg-[#0B1F40] text-white text-[10px] font-black tracking-[0.15em] uppercase h-8 px-3 hover:bg-[#E50B15] transition-colors disabled:opacity-50">
                {gpsLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Locate className="h-3 w-3" />}
                USAR MI UBICACIÓN
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">PAÍS *</Label>
              <Select value={form.country} onValueChange={v => set('country', v)}>
                <SelectTrigger data-testid="denuncia-country" className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] mt-1 h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none border-2 border-[#0B1F40]">{COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">ESTADO</Label>
              <Select value={form.state} onValueChange={v => set('state', v)}>
                <SelectTrigger data-testid="denuncia-state" className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] mt-1 h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none border-2 border-[#0B1F40]">{statesList.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">MUNICIPIO / CIUDAD</Label>
              <Input data-testid="denuncia-municipality" value={form.municipality} onChange={e => set('municipality', e.target.value)}
                className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 h-10" />
            </div>
            <div>
              <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">CALLE / COLONIA / REFERENCIA</Label>
              <Input data-testid="denuncia-street" value={form.street} onChange={e => set('street', e.target.value)}
                className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 h-10" />
            </div>
          </div>
        </div>

        {/* Media */}
        <div className="border-t-2 border-[#0B1F40]/10 pt-5">
          <h3 className="text-[10px] font-black tracking-[0.3em] uppercase text-[#E50B15] mb-3">EVIDENCIA (OPCIONAL)</h3>

          {/* Images */}
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-2">
              <input ref={imgRef} type="file" accept="image/*" multiple onChange={addImages} className="hidden" />
              <button type="button" data-testid="denuncia-upload-image" onClick={() => imgRef.current?.click()} disabled={upImg || images.length >= 5}
                className="border-2 border-[#0B1F40]/20 text-[#0B1F40] text-[10px] font-black h-8 px-3 flex items-center gap-1.5 hover:border-[#0B1F40] disabled:opacity-40">
                {upImg ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageIcon className="h-3 w-3" />} SUBIR IMÁGENES
              </button>
              <span className="text-[9px] text-[#0B1F40]/40 font-bold">{images.length}/5 — máx 10MB c/u</span>
            </div>
            {images.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {images.map(p => (
                  <div key={p} className="relative aspect-square border border-[#0B1F40]/20 overflow-hidden bg-[#F4F5F7]">
                    <img src={getFileUrl(p)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeImg(p)}
                      className="absolute top-1 right-1 bg-[#E50B15] text-white h-5 w-5 flex items-center justify-center">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Videos */}
          <div>
            <div className="flex items-center gap-3 mb-2">
              <input ref={vidRef} type="file" accept="video/*" multiple onChange={addVideos} className="hidden" />
              <button type="button" data-testid="denuncia-upload-video" onClick={() => vidRef.current?.click()} disabled={upVid || videos.length >= 2}
                className="border-2 border-[#0B1F40]/20 text-[#0B1F40] text-[10px] font-black h-8 px-3 flex items-center gap-1.5 hover:border-[#0B1F40] disabled:opacity-40">
                {upVid ? <Loader2 className="h-3 w-3 animate-spin" /> : <VideoIcon className="h-3 w-3" />} SUBIR VIDEOS
              </button>
              <span className="text-[9px] text-[#0B1F40]/40 font-bold">{videos.length}/2 — máx 50MB c/u</span>
            </div>
            {videos.length > 0 && (
              <div className="space-y-2">
                {videos.map(p => (
                  <div key={p} className="flex items-center gap-2 border border-[#0B1F40]/20 px-3 py-2 bg-[#F4F5F7]">
                    <VideoIcon className="h-4 w-4 text-[#0B1F40]/40" />
                    <span className="text-[10px] text-[#0B1F40]/60 flex-1 truncate font-bold">{p.split('/').pop()}</span>
                    <button type="button" onClick={() => removeVid(p)} className="bg-[#E50B15] text-white h-5 w-5 flex items-center justify-center"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Contact (optional) */}
        <div className="border-t-2 border-[#0B1F40]/10 pt-5">
          <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">CONTACTO (OPCIONAL — SOLO SI QUIERES RESPUESTA)</Label>
          <Input data-testid="denuncia-contact" value={form.contact} onChange={e => set('contact', e.target.value)}
            placeholder="Email o teléfono (opcional)"
            className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 h-10" />
          <p className="text-[9px] text-[#0B1F40]/30 mt-1">Déjalo en blanco para permanecer 100% anónimo.</p>
        </div>

        <button data-testid="denuncia-submit" type="submit" disabled={sending}
          className="w-full bg-[#E50B15] text-white font-black text-sm h-12 flex items-center justify-center gap-2 brutal-btn-press transition-all mt-2" style={{ boxShadow: '4px 4px 0px #0B1F40' }}>
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} ENVIAR DENUNCIA
        </button>
      </form>
    </div>
  );
}
